// ClientDetail — the funding workspace for one client.
//
// Tabs:
//   overview      — contact + business profile + contract (percentage picker)
//                   + one-click Send-to-BRM
//   qualification — import report → run the locked qualification analysis →
//                   both funding paths, checks, positives/negatives, summary
//   lenders       — match lenders against the analysis + intake, Apply-now
//                   buttons (new tab) + copy-paste data panel
import {
  Banknote, CheckCircle2, ClipboardCopy, Download, ExternalLink, FileSignature,
  FileUp, History, Landmark, Mail, Pencil, RefreshCw, Search, Send, ShieldAlert, XCircle,
  Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BusinessAppModal } from "../components/BusinessAppModal";
import { CreditKarmaCredentials } from "../components/CreditKarmaCredentials";
import { MonitoringCredentials } from "../components/MonitoringCredentials";
import { RequestDocumentsDialog } from "../components/RequestDocumentsDialog";
import { RevealableField } from "../lib/pii";
import { EditClientDialog } from "../components/EditClientDialog";
import { ParsedReportModal } from "../components/ParsedReportModal";
import { extractPdfText, fileToBase64 } from "../lib/pdf-extract";
import { SendIntakeDialog } from "../components/SendIntakeDialog";
import { Shell } from "../components/Shell";
import { contractTemplates as contractTemplatesApi, openAuthenticatedFile, type ContractTemplate } from "../lib/api";
import { FundingApplications } from "../components/FundingApplications";
import { StatusPill } from "../components/StatusPill";
import { PastApprovalsDialog, RecordOutcomeDialog } from "../components/ApprovalDialogs";
import {
  approvals as approvalsApi,
  brm as brmApi,
  brmReps as brmRepsApi,
  businessApplications as businessAppsApi,
  clients as clientsApi,
  recommendations as recsApi,
  reports as reportsApi,
  type ApprovalRecord,
  type BrmRep,
  type CkPrequalTier,
  type Client,
  type CreditReport,
  type LenderMatch,
} from "../lib/api";

type Tab = "overview" | "qualification" | "lenders" | "applications";

const PERCENTAGES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 9.99, 10];

const money = (n: unknown) =>
  n === null || n === undefined || n === "" ? "—"
    : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function VerdictBadge({ label, eligible }: { label: string; eligible: boolean }) {
  return (
    <div className="sub-panel" style={{ textAlign: "center" }}>
      <p className="muted small" style={{ textTransform: "uppercase", letterSpacing: "0.12em" }}>{label}</p>
      <span className={`pill ${eligible ? "pill-green" : "pill-red"}`} style={{ fontSize: 14, padding: "8px 20px" }}>
        {eligible ? "FUNDING READY" : "NOT YET"}
      </span>
    </div>
  );
}

function ChecksTable({ checks }: { checks: any[] }) {
  return (
    <div className="table-list">
      {checks.map((c: any) => (
        <div className="table-row table-row--wide" key={c.id}>
          {c.pass
            ? <CheckCircle2 size={16} style={{ color: "var(--ne-success)" }} aria-hidden="true" />
            : <XCircle size={16} style={{ color: "var(--ne-red-bright)" }} aria-hidden="true" />}
          <strong style={{ flex: "1 1 220px" }}>{c.label}</strong>
          <span className="muted small">needs {c.required}</span>
          <span style={{ fontWeight: 600 }}>{c.actual}</span>
          {c.detail && <span className="muted small" style={{ flexBasis: "100%" }}>{c.detail}</span>}
        </div>
      ))}
    </div>
  );
}

// Employment & finances (migration 042) — formatting shared by the profile
// panel and the lender copy-paste panel.
function tenure(c: any): string {
  const y = Number(c?.employment_years), m = Number(c?.employment_months);
  const parts: string[] = [];
  if (Number.isFinite(y) && y > 0) parts.push(`${y} yr${y === 1 ? "" : "s"}`);
  if (Number.isFinite(m) && m > 0) parts.push(`${m} mo`);
  return parts.join(" ");
}
function otherIncomeRows(c: any): Array<{ source: string; amount: number; frequency: string }> {
  return Array.isArray(c?.other_income) ? c.other_income.filter((o: any) => o && o.source) : [];
}

export default function ClientDetailPage() {
  const { id = "" } = useParams();
  // Always the current route id — lets async loads drop a response that lands
  // after the route already moved on.
  const routeIdRef = useRef(id);
  routeIdRef.current = id;
  // Apply a client object to state only if it still matches the current route,
  // so a mutation/reveal response that resolves after navigation (or after the
  // card unmounted) can never overwrite state with the previous client and
  // wedge the page on "Loading…".
  const applyClient = (u: Client | null) => { if (u && u.id === routeIdRef.current) setClient(u); };
  const nav = useNavigate();

  const [client, setClient] = useState<Client | null>(null);
  const [tab, setTab] = useState<Tab>("overview");
  const [err, setErr] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [reqDocsOpen, setReqDocsOpen] = useState(false);
  const [appMsg, setAppMsg] = useState<string | null>(null);
  const [appSending, setAppSending] = useState(false);
  const [bizApps, setBizApps] = useState<Array<{ id: string; status: string; business_name: string | null; submitted_at: string | null; created_at: string }>>([]);
  const [openBizAppId, setOpenBizAppId] = useState<string | null>(null);
  const [clientDocs, setClientDocs] = useState<Array<{ id: string; original_filename: string; source_hint: string | null; uploaded_at: string; download_path: string }>>([]);

  async function openClientDoc(path: string, filename: string) {
    try {
      const token = localStorage.getItem("nedm.staff.token");
      const res = await fetch(path, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
      if (!res.ok) throw new Error(`Could not open (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) { setErr(e?.message || "Could not download the document."); }
  }

  // Delete one report from this client. Permanent — captured file + analysis
  // go with it — so it always asks first.
  async function deleteReport(r: CreditReport) {
    if (!window.confirm(
      `Delete this ${r.provider.replace(/_/g, " ")} report?\n\nThis permanently removes the captured report and its analysis. It cannot be undone.`,
    )) return;
    try {
      await reportsApi.remove(r.id);
      setReportsList((prev) => prev.filter((x) => x.id !== r.id));
    } catch (e: any) { setErr(e?.message || "Could not delete the report."); }
  }

  // Delete one stored document (file + record). Permanent, so it asks first.
  async function deleteDoc(d: any) {
    if (!window.confirm(`Delete "${d.original_filename}"?\n\nThis permanently removes the stored file. It cannot be undone.`)) return;
    try {
      await clientsApi.removeUpload(id, d.id);
      setClientDocs((prev) => prev.filter((x: any) => x.id !== d.id));
    } catch (e: any) { setErr(e?.message || "Could not delete the document."); }
  }

  async function sendBusinessApplication() {
    if (!id) return;
    setAppSending(true); setAppMsg(null);
    try {
      const r = await businessAppsApi.send(id, client?.email || undefined);
      const sent = r.email?.mode === "resend";
      setAppMsg(`${sent ? "Business application emailed" : "Business application created"}. Link: ${r.application.apply_url}`);
    } catch (e: any) {
      setAppMsg(e?.message || "Could not send the business application.");
    }
    setAppSending(false);
  }

  // qualification state
  const [reportsList, setReportsList] = useState<CreditReport[]>([]);
  const [analysis, setAnalysis] = useState<any | null>(null);
  const [importing, setImporting] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [viewReportId, setViewReportId] = useState<string | null>(null);
  const [emailMsg, setEmailMsg] = useState<string | null>(null);
  const [emailingAnalysis, setEmailingAnalysis] = useState(false);

  // contract state
  const [pct, setPct] = useState<number>(5);
  const [sendingContract, setSendingContract] = useState(false);
  const [contractMsg, setContractMsg] = useState<string | null>(null);
  // customizable contract templates — the picker defaults to the tenant default
  const [contractTpls, setContractTpls] = useState<ContractTemplate[]>([]);
  const [tplId, setTplId] = useState<string>("");
  useEffect(() => {
    contractTemplatesApi.list().then((list) => {
      setContractTpls(list);
      const def = list.find((t) => t.is_default) || list[0];
      if (def) {
        setTplId(def.id);
        if (def.backend_percentage != null) setPct(Number(def.backend_percentage));
      }
    }).catch(() => {});
  }, []);

  // BRM state
  const [reps, setReps] = useState<BrmRep[]>([]);
  const [repId, setRepId] = useState<string>("");
  const [brmNote, setBrmNote] = useState("");
  const [sendingBrm, setSendingBrm] = useState(false);
  const [brmMsg, setBrmMsg] = useState<string | null>(null);

  // lender matching state
  const [matches, setMatches] = useState<LenderMatch[]>([]);
  const [matching, setMatching] = useState(false);
  const [matchErr, setMatchErr] = useState<string | null>(null);

  // approval database + CK pre-qual tier
  const [ck, setCk] = useState<CkPrequalTier | null>(null);
  const [approvalsList, setApprovalsList] = useState<ApprovalRecord[]>([]);
  const [outcomeMatch, setOutcomeMatch] = useState<LenderMatch | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  async function loadApprovals() {
    try { setApprovalsList(await approvalsApi.list(id)); } catch {}
  }

  async function loadAll() {
    const reqId = id;
    try {
      const fresh = await clientsApi.get(id);
      if (routeIdRef.current !== reqId) return; // route changed mid-fetch — drop
      setClient(fresh);
    } catch (e: any) { if (routeIdRef.current === reqId) setErr(e?.message || "Client not found"); return; }
    try {
      const list = await reportsApi.list(id);
      setReportsList(list);
      // Pull the stored analysis from the newest report that has one.
      for (const r of list) {
        try { setAnalysis(await reportsApi.getAnalysis(r.id)); break; } catch {}
      }
    } catch {}
    try {
      const rr = await brmRepsApi.list();
      setReps(rr);
      const def = rr.find((r) => r.is_default) || rr[0];
      if (def) setRepId(def.id);
    } catch {}
    try { setBizApps(await businessAppsApi.listForClient(id)); } catch {}
    try {
      const r = await recsApi.latest(id);
      if (r.matches?.length) setMatches(r.matches);
      setCk(r.credit_karma ?? null);
    } catch {}
    loadApprovals();
    try {
      const token = localStorage.getItem("nedm.staff.token");
      const r = await fetch(`/api/clients/${id}/uploads`, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
      const d = await r.json();
      if (d.ok) setClientDocs(d.uploads || []);
    } catch {}
  }
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  const c: any = client || {};

  const copyPanelText = useMemo(() => {
    return [
      `Name: ${c.legal_name ?? ""}`,
      `Email: ${c.email ?? ""}`,
      `Phone: ${c.phone ?? ""}`,
      `Address: ${[c.address_line1, c.address_city, c.address_state, c.address_zip].filter(Boolean).join(", ")}`,
      `Business: ${c.business_name ?? ""}`,
      `EIN: ${c.business_ein ?? ""}`,
      `Entity: ${c.business_entity_type ?? ""}`,
      `Industry: ${c.business_industry ?? ""}`,
      `Business location: ${[c.business_city, c.business_state, c.business_zip].filter(Boolean).join(", ")}`,
      `Started: ${c.business_start_date ?? ""}`,
      `Annual revenue: ${c.annual_revenue ?? ""}`,
      `Employer: ${c.employer_name ?? ""}`,
      `Employer address: ${c.employer_address ?? ""}`,
      `Employer phone: ${c.employer_phone ?? ""}`,
      `Time at job: ${tenure(c)}`,
      `Annual salary: ${c.annual_income ?? ""}`,
      ...otherIncomeRows(c).map((o, i) => `Other income ${i + 1}: ${o.source} — ${o.amount} per ${o.frequency === "annual" ? "year" : "month"}`),
      `Monthly housing cost: ${c.housing_payment ?? ""}`,
      `Checking balance: ${c.checking_balance ?? ""}`,
      `Savings balance: ${c.savings_balance ?? ""}`,
      `Capital requested: ${c.capital_requested ?? ""}`,
      `Use of funds: ${c.use_of_funds ?? ""}`,
    ].join("\n");
  }, [client]);

  async function importReport() {
    setImporting(true); setErr(null);
    try {
      await reportsApi.importFromCredentials(id);
      setReportsList(await reportsApi.list(id));
    } catch (e: any) {
      // A failed scrape now returns 502 with a step-specific message — show
      // that message so a failed (paid) import never looks like a success.
      setErr(e?.message || "Credit report import failed — are monitoring credentials on file?");
      // The server still stored a diagnostic 'failed' report row; refresh the
      // list so staff can see it, without clobbering the error message above.
      try { setReportsList(await reportsApi.list(id)); } catch { /* keep the import error */ }
    } finally { setImporting(false); }
  }

  async function uploadPdfReport(file: File) {
    setUploadingPdf(true); setErr(null);
    try {
      const { text, imageOnly } = await extractPdfText(file);
      if (imageOnly || !text.trim()) {
        setErr("That PDF has no readable text — it looks like a scan or screenshot. Use the site's \"Save/Print to PDF\" instead.");
        return;
      }
      const pdf_base64 = await fileToBase64(file).catch(() => undefined);
      const res = await reportsApi.uploadPdf(id, { text, pdf_base64, filename: file.name });
      setReportsList(await reportsApi.list(id));
      const sc = res.scores || {};
      const shown = ["transunion", "experian", "equifax"].map((b) => (sc as any)[b]).filter(Boolean);
      setErr(null);
      if (!shown.length) {
        setErr(`Uploaded (${res.provider_label}), but no bureau score was read from this layout. It's saved — send this PDF to support so this report layout can be added.`);
      }
    } catch (e: any) {
      setErr(e?.message || "Could not read that PDF.");
    } finally { setUploadingPdf(false); }
  }

  async function runAnalysis() {
    const target = reportsList[0];
    if (!target) { setErr("Import a credit report first."); return; }
    setAnalyzing(true); setErr(null);
    try {
      setAnalysis(await reportsApi.analyze(target.id));
      setTab("qualification");
    } catch (e: any) {
      setErr(e?.message || "Analysis failed");
    } finally { setAnalyzing(false); }
  }

  async function emailAnalysis() {
    if (emailingAnalysis) return;
    setEmailingAnalysis(true); setEmailMsg(null);
    try {
      const r = await clientsApi.sendAnalysis(id, { confirm: true, recipient_email: c.email });
      setEmailMsg(`Sent to ${r.recipient_email}.`);
    } catch (e: any) {
      setEmailMsg(e?.message || "Send failed");
    } finally { setEmailingAnalysis(false); }
  }

  async function sendContract() {
    setSendingContract(true); setContractMsg(null);
    try {
      const tpl = contractTpls.find((t) => t.id === tplId) || null;
      const r = await clientsApi.sendServiceAgreement(id, {
        confirm: true,
        percentage: pct,
        ...(tpl ? { template_id: tpl.id } : {}),
      } as any);
      setContractMsg(`Sent the ${pct}% contract${tpl ? ` (${tpl.name})` : ""} to ${r.recipient_email}.`);
      applyClient(await clientsApi.get(id));
    } catch (e: any) {
      setContractMsg(e?.message || "Send failed");
    } finally { setSendingContract(false); }
  }

  async function sendToBrm() {
    setSendingBrm(true); setBrmMsg(null);
    try {
      const r = await brmApi.send(id, { confirm: true, brm_rep_id: repId || undefined, note: brmNote || undefined });
      setBrmMsg(`Sent to ${r.rep.name} (${r.rep.bank_name}).`);
      setBrmNote("");
    } catch (e: any) {
      setBrmMsg(e?.message || "Send failed");
    } finally { setSendingBrm(false); }
  }

  async function matchLenders() {
    setMatching(true); setMatchErr(null);
    try {
      const r = await recsApi.generate(id);
      setMatches(r.matches);
      setCk(r.credit_karma ?? null);
      setTab("lenders");
    } catch (e: any) {
      setMatchErr(e?.message || "Matching failed — run the qualification analysis first.");
    } finally { setMatching(false); }
  }

  const fr = analysis?.funding_readiness;

  // Never render a client whose id does not match the current route — during
  // navigation the previous client lingers in state, and rendering it would
  // briefly show that client's revealed SSN/DOB/credentials under the new
  // route (or keep them visible if the next fetch fails). Show a light
  // loading state until the matching client is in hand.
  if (!client || client.id !== id) {
    return (
      <Shell title="Client" subtitle="Funding workspace">
        {err ? <p className="error-text">{err}</p> : <p className="muted small">Loading…</p>}
      </Shell>
    );
  }

  return (
    <Shell title={c.legal_name || "Client"} subtitle="Funding workspace" actions={
      <div style={{ display: "flex", gap: 8 }}>
        <button className="ghost-button" onClick={() => setIntakeOpen(true)}><Send size={14} aria-hidden="true" /> Intake link</button>
        <button className="ghost-button" onClick={() => setReqDocsOpen(true)}><FileUp size={14} aria-hidden="true" /> Request documents</button>
        <button className="ghost-button" onClick={sendBusinessApplication} disabled={appSending}><FileSignature size={14} aria-hidden="true" /> {appSending ? "Sending…" : "Business application"}</button>
        <button className="ghost-button" onClick={() => setEditOpen(true)}><Pencil size={14} aria-hidden="true" /> Edit</button>
      </div>
    }>
      {err && <p className="error-text">{err}</p>}
      {appMsg && <p className="muted small" style={{ wordBreak: "break-all" }}>{appMsg}</p>}

      <div className="profile-tabs" role="tablist" aria-label="Client sections" style={{ marginBottom: 16 }}>
        {(["overview", "qualification", "lenders", "applications"] as Tab[]).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? "selected" : undefined} onClick={() => setTab(t)}>
            {t === "overview" ? "Overview" : t === "qualification" ? "Qualification" : t === "lenders" ? "Lender Match" : "Applications"}
          </button>
        ))}
      </div>

      {/* ─────────────── OVERVIEW ─────────────── */}
      {tab === "overview" && client && (
        <>
          <section className="main-grid main-grid--pair">
            <div className="panel">
              <div className="panel-header"><div><h2>Client</h2></div><StatusPill status={client.status} /></div>
              <div className="profile-metrics">
                <div><p>Email</p><strong style={{ fontSize: 14 }}>{client.email}</strong></div>
                <div><p>Phone</p><strong style={{ fontSize: 14 }}>{client.phone || "—"}</strong></div>
                <div><p>DOB</p><strong style={{ fontSize: 14 }}><RevealableField clientId={client.id} field="dob" maskedValue={client.dob_masked} /></strong></div>
                <div><p>SSN</p><strong style={{ fontSize: 14 }}><RevealableField clientId={client.id} field="ssn" maskedValue={client.ssn_masked} /></strong></div>
                <div><p>Monitoring</p><strong style={{ fontSize: 14 }}>{client.monitoring === "not_started" ? "Not set up" : client.monitoring.replace(/_/g, " ")}</strong></div>
                <div><p>Address</p><strong style={{ fontSize: 14 }}>{[c.address_line1, c.address_city, c.address_state].filter(Boolean).join(", ") || "—"}</strong></div>
              </div>
            </div>

            <div className="panel">
              <div className="panel-header"><div><h2>Business & funding request</h2></div></div>
              <div className="profile-metrics">
                <div><p>Business</p><strong style={{ fontSize: 14 }}>{c.business_name || "—"}</strong></div>
                <div><p>Entity / EIN</p><strong style={{ fontSize: 14 }}>{[c.business_entity_type, c.business_ein].filter(Boolean).join(" · ") || "—"}</strong></div>
                <div><p>Industry</p><strong style={{ fontSize: 14 }}>{c.business_industry || "—"}</strong></div>
                <div><p>Location</p><strong style={{ fontSize: 14 }}>{[c.business_city, c.business_state].filter(Boolean).join(", ") || "—"}</strong></div>
                <div><p>Started</p><strong style={{ fontSize: 14 }}>{c.business_start_date ? String(c.business_start_date).slice(0, 10) : "—"}</strong></div>
                <div><p>Annual revenue</p><strong style={{ fontSize: 14 }}>{money(c.annual_revenue)}</strong></div>
                <div><p>Capital requested</p><strong style={{ fontSize: 14, color: "var(--ne-red-bright)" }}>{money(c.capital_requested)}</strong></div>
                <div><p>Use of funds</p><strong style={{ fontSize: 14 }}>{c.use_of_funds || "—"}</strong></div>
              </div>
              {c.business_description && <p className="body-copy" style={{ marginTop: 10 }}>{c.business_description}</p>}
            </div>

            <div className="panel">
              <div className="panel-header"><div><h2>Employment &amp; finances</h2></div></div>
              <div className="profile-metrics">
                <div><p>Employer</p><strong style={{ fontSize: 14 }}>{c.employer_name || "—"}</strong></div>
                <div><p>Employer phone</p><strong style={{ fontSize: 14, whiteSpace: "nowrap" }}>{c.employer_phone || "—"}</strong></div>
                <div><p>Employer address</p><strong style={{ fontSize: 14 }}>{c.employer_address || "—"}</strong></div>
                <div><p>Time at job</p><strong style={{ fontSize: 14 }}>{tenure(c) || "—"}</strong></div>
                <div><p>Annual salary</p><strong style={{ fontSize: 14 }}>{money(c.annual_income)}</strong></div>
                <div><p>Monthly housing</p><strong style={{ fontSize: 14 }}>{money(c.housing_payment)}</strong></div>
                <div><p>Checking balance</p><strong style={{ fontSize: 14 }}>{money(c.checking_balance)}</strong></div>
                <div><p>Savings balance</p><strong style={{ fontSize: 14 }}>{money(c.savings_balance)}</strong></div>
              </div>
              <p className="muted small" style={{ margin: "12px 0 6px" }}>Other income</p>
              {otherIncomeRows(c).length ? (
                <div className="profile-metrics">
                  {otherIncomeRows(c).map((o, i) => (
                    <div key={i}><p>{o.source}</p><strong style={{ fontSize: 14 }}>{money(o.amount)} / {o.frequency === "annual" ? "yr" : "mo"}</strong></div>
                  ))}
                </div>
              ) : <p className="muted small">None reported</p>}
            </div>
          </section>

          {client.monitoring !== "not_started" && (
            <section style={{ marginTop: 16 }}>
              <MonitoringCredentials client={client} onSaved={applyClient} />
            </section>
          )}

          <section style={{ marginTop: 16 }}>
            <CreditKarmaCredentials client={client} onSaved={applyClient} />
          </section>

          {clientDocs.length > 0 && (
            <section style={{ marginTop: 16 }}>
              <div className="panel">
                <div className="panel-header">
                  <div><h2><Download size={18} aria-hidden="true" style={{ verticalAlign: "-3px" }} /> Client documents</h2>
                  <span className="muted small">Signed forms and uploads on file — click to download.</span></div>
                </div>
                {clientDocs.map((d) => (
                  <div key={d.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 0", borderTop: "1px solid var(--ne-border, #2a2a2a)", flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      {d.source_hint === "voe_signed" && <span className="pill pill-green">Signed</span>}
                      <strong style={{ fontSize: 14 }}>{d.original_filename}</strong>
                      <span className="muted small">{String(d.uploaded_at).slice(0, 10)}</span>
                    </div>
                    <span style={{ display: "inline-flex", gap: 6 }}>
                      <button className="ghost-button" aria-label={`Download ${d.original_filename}`} onClick={() => openClientDoc(d.download_path, d.original_filename)}>
                        <Download size={14} aria-hidden="true" /> Download
                      </button>
                      <button
                        className="icon-button danger"
                        title="Delete this document"
                        aria-label={`Delete ${d.original_filename}`}
                        onClick={() => deleteDoc(d)}
                        style={{ height: 32, padding: "0 9px" }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {bizApps.length > 0 && (
            <section style={{ marginTop: 16 }}>
              <div className="panel">
                <div className="panel-header">
                  <div><h2><FileSignature size={18} aria-hidden="true" style={{ verticalAlign: "-3px" }} /> Funding applications</h2>
                  <span className="muted small">Business Funding Applications sent to this client. View shows every answer; SSNs stay masked until you reveal them.</span></div>
                </div>
                {bizApps.map((a) => (
                  <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 0", borderTop: "1px solid var(--ne-border, #2a2a2a)", flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span className={`pill ${a.status === "submitted" ? "pill-green" : a.status === "started" ? "" : "pill-red"}`}>{a.status}</span>
                      <strong style={{ fontSize: 14 }}>{a.business_name || "—"}</strong>
                      <span className="muted small">
                        {a.submitted_at ? `submitted ${String(a.submitted_at).slice(0, 10)}` : `sent ${String(a.created_at).slice(0, 10)}`}
                      </span>
                    </div>
                    <button className="ghost-button" aria-label={`View application${a.business_name ? ` for ${a.business_name}` : ""}`} onClick={() => setOpenBizAppId(a.id)}>
                      <Search size={14} aria-hidden="true" /> View / edit
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="main-grid main-grid--pair" style={{ marginTop: 16 }}>
            <div className="panel">
              <div className="panel-header">
                <div><h2><FileSignature size={18} aria-hidden="true" style={{ verticalAlign: "-3px" }} /> Funding contract</h2>
                <span className="muted small">Pick the back-end percentage for this client — every deal carries different legwork.</span></div>
              </div>
              <p className="body-copy">
                Status:{" "}
                {client.service_agreement_signed
                  ? <span className="pill pill-green">Signed</span>
                  : client.service_agreement && client.service_agreement.state === "pending"
                    ? <span className="pill">Sent — awaiting signature</span>
                    : <span className="pill pill-red">Not sent</span>}
              </p>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
                {contractTpls.length > 0 && (
                  <select className="text-input" style={{ maxWidth: 240 }} value={tplId} aria-label="Contract template"
                    onChange={(e) => {
                      setTplId(e.target.value);
                      const t = contractTpls.find((x) => x.id === e.target.value);
                      if (t?.backend_percentage != null) setPct(Number(t.backend_percentage));
                    }}>
                    {contractTpls.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}{t.backend_percentage != null ? ` (${t.backend_percentage}%)` : ""}</option>
                    ))}
                  </select>
                )}
                {tplId ? (
                  <>
                    {/* Custom template: any percentage 0–100 (each tenant sets their own).
                        The datalist keeps the standard tiers as quick-pick presets. */}
                    <input type="number" className="text-input" style={{ width: 110 }}
                      min={0} max={100} step={0.01} list="contract-pct-presets"
                      value={Number.isFinite(pct) ? pct : ""}
                      onChange={(e) => setPct(e.target.value === "" ? NaN : Number(e.target.value))}
                      aria-label="Fee percentage (0–100)" />
                    <datalist id="contract-pct-presets">
                      {PERCENTAGES.map((p) => <option key={p} value={p} />)}
                    </datalist>
                  </>
                ) : (
                  <select className="text-input" style={{ width: 140 }} value={pct} onChange={(e) => setPct(Number(e.target.value))} aria-label="Fee percentage">
                    {PERCENTAGES.map((p) => <option key={p} value={p}>{p}%</option>)}
                  </select>
                )}
                <button className="primary-button"
                  disabled={sendingContract || !(Number.isFinite(pct) && pct >= 0 && pct <= 100)}
                  onClick={sendContract}>
                  {sendingContract ? "Sending…" : Number.isFinite(pct) ? `Send ${pct}% contract` : "Send contract"}
                </button>
                {!client.service_agreement_signed && client.service_agreement?.state === "pending" && (
                  <button className="ghost-button" onClick={async () => {
                    if (!confirm("Revoke the outstanding contract link?")) return;
                    try { await clientsApi.revokeServiceAgreement(id); applyClient(await clientsApi.get(id)); } catch (e: any) { alert(e?.message); }
                  }}>Revoke</button>
                )}
              </div>
              {contractMsg && <p className="muted small" style={{ marginTop: 8 }}>{contractMsg}</p>}
              {!analysis && (
                <p className="muted small" style={{ marginTop: 8 }}>
                  <ShieldAlert size={13} aria-hidden="true" style={{ verticalAlign: "-2px" }} />{" "}
                  Tip: run the qualification first — catch bad credit before you send a contract.
                </p>
              )}
            </div>

            <div className="panel">
              <div className="panel-header">
                <div><h2><Banknote size={18} aria-hidden="true" style={{ verticalAlign: "-3px" }} /> Send to BRM</h2>
                <span className="muted small">One click packages this client's funding file to your business relationship manager.</span></div>
              </div>
              {reps.length === 0 ? (
                <p className="body-copy">
                  No BRM reps configured yet.{" "}
                  <button className="ghost-button" onClick={() => nav("/lenders")}>Add one on the Lenders page →</button>
                </p>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <select className="text-input" style={{ flex: "1 1 220px" }} value={repId} onChange={(e) => setRepId(e.target.value)}>
                      {reps.filter(r => r.active).map((r) => (
                        <option key={r.id} value={r.id}>{r.name} — {r.bank_name}</option>
                      ))}
                    </select>
                    <input className="text-input" style={{ flex: "2 1 260px" }} placeholder="Note for the rep (optional)" value={brmNote} onChange={(e) => setBrmNote(e.target.value)} />
                  </div>
                  <button className="primary-button" style={{ marginTop: 10 }} disabled={sendingBrm} onClick={sendToBrm}>
                    {sendingBrm ? "Sending…" : "Send client file to BRM"}
                  </button>
                  {brmMsg && <p className="muted small" style={{ marginTop: 8 }}>{brmMsg}</p>}
                </>
              )}
            </div>
          </section>

          {/* Documents uploaded during intake — staff-authed View links (v1.1 port). */}
          <div className="panel" style={{ marginTop: 16 }}>
            <div className="panel-header">
              <div><h2>Documents (from intake)</h2>
              <span className="muted small">Government ID, Secretary of State filing, EIN letter, and anything else the client uploaded.</span></div>
            </div>
            <div className="table-list">
              {(client.identity_documents ?? []).map((d) => (
                <div className="table-row table-row--wide" key={d.id}>
                  <strong style={{ textTransform: "capitalize", flex: "1 1 220px" }}>{d.kind.replace(/_/g, " ")}</strong>
                  <span className="muted small">{(d.uploaded_at || "").slice(0, 10)}</span>
                  {d.verified_at && <span className="pill pill-green">verified</span>}
                  {d.view_url ? (
                    <button className="ghost-button" onClick={async () => {
                      try { await openAuthenticatedFile(d.view_url!, `${d.kind}.jpg`); }
                      catch (e: any) { setErr(e?.message || "Could not open that document."); }
                    }}>View</button>
                  ) : (
                    <span className="muted small">unavailable</span>
                  )}
                </div>
              ))}
              {(client.identity_documents ?? []).length === 0 && (
                <p className="muted small">Nothing uploaded yet — documents land here when the client completes intake.</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* ─────────────── QUALIFICATION ─────────────── */}
      {tab === "qualification" && (
        <>
          <div className="panel">
            <div className="panel-header">
              <div><h2>Credit reports</h2>
              <span className="muted small">Client signs up for monitoring through your affiliate link → import pulls the full 3-bureau report.</span></div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="ghost-button" disabled={importing} onClick={importReport}>
                  <Download size={14} aria-hidden="true" /> {importing ? "Importing… (up to 2 min)" : "Import report"}
                </button>
                <label className="ghost-button" style={{ cursor: uploadingPdf ? "default" : "pointer" }}>
                  <FileUp size={14} aria-hidden="true" /> {uploadingPdf ? "Reading PDF…" : "Upload PDF"}
                  <input type="file" accept="application/pdf,.pdf" hidden disabled={uploadingPdf}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPdfReport(f); e.target.value = ""; }} />
                </label>
                <button className="primary-button" disabled={analyzing || reportsList.length === 0} onClick={runAnalysis}>
                  <RefreshCw size={14} aria-hidden="true" /> {analyzing ? "Analyzing…" : "Run qualification"}
                </button>
              </div>
            </div>
            <div className="table-list">
              {reportsList.map((r) => (
                <div className="table-row table-row--wide" key={r.id}>
                  <span>{(r.imported_at || "").slice(0, 10)}</span>
                  <strong>{r.provider.replace(/_/g, " ")}</strong>
                  <StatusPill status={r.parse_status} kind="report" />
                  <button className="ghost-button" onClick={() => setViewReportId(r.id)}>View report</button>
                  <button
                    className="icon-button danger"
                    title="Delete this report"
                    aria-label={`Delete ${r.provider.replace(/_/g, " ")} report`}
                    onClick={() => deleteReport(r)}
                    style={{ height: 30, padding: "0 9px" }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {reportsList.length === 0 && <p className="muted small">No reports yet — import one to begin.</p>}
            </div>
          </div>

          {analysis && analysis.summary && analysis.summary.report_complete === false && (
            <section className="panel" style={{ marginTop: 16, borderLeft: "4px solid var(--ne-warning-border)", background: "var(--ne-warning-bg)" }}>
              <h2 style={{ marginTop: 0 }}>⚠ Full credit report required</h2>
              <p style={{ margin: "6px 0 0" }}>
                {analysis.summary.incomplete_reason || "This report only has scores — no account detail was retrieved. Funding cannot be judged from scores alone."}
              </p>
              <p className="muted small" style={{ margin: "10px 0 0" }}>
                Scores on file: {["transunion","experian","equifax"].map((b) => (analysis.scores?.[b] ? `${b.slice(0,2).toUpperCase()} ${analysis.scores[b]}` : null)).filter(Boolean).join(" · ") || "none"}.
                Import or upload a full report (all accounts) to run qualification.
              </p>
            </section>
          )}
          {analysis && fr && analysis.summary?.report_complete !== false && (
            <>
              <section className="split-section" style={{ marginTop: 16 }}>
                <VerdictBadge label="Personal funding (670+)" eligible={!!fr.personal.eligible} />
                <VerdictBadge label="Business funding (700+)" eligible={!!fr.business.eligible} />
              </section>

              <section className="main-grid main-grid--pair" style={{ marginTop: 16 }}>
                <div className="panel">
                  <div className="panel-header"><div><h2>Personal path checks</h2></div></div>
                  <ChecksTable checks={fr.personal.checks || []} />
                </div>
                <div className="panel">
                  <div className="panel-header"><div><h2>Business path checks</h2></div></div>
                  <ChecksTable checks={fr.business.checks || []} />
                </div>
              </section>

              <section className="main-grid main-grid--pair" style={{ marginTop: 16 }}>
                <div className="panel">
                  <div className="panel-header"><div><h2 style={{ color: "var(--ne-success-text)" }}>What's helping</h2></div></div>
                  <ul className="body-copy" style={{ paddingLeft: 20 }}>
                    {(analysis.positives || []).map((p: string, i: number) => <li key={i}>{p}</li>)}
                    {(analysis.positives || []).length === 0 && <li className="muted">Nothing yet.</li>}
                  </ul>
                </div>
                <div className="panel">
                  <div className="panel-header"><div><h2 style={{ color: "var(--ne-red-bright)" }}>What's hurting</h2></div></div>
                  <ul className="body-copy" style={{ paddingLeft: 20 }}>
                    {(analysis.weaknesses || []).map((w: string, i: number) => <li key={i}>{w}</li>)}
                    {(analysis.weaknesses || []).length === 0 && <li className="muted">Clean profile — no negatives found.</li>}
                  </ul>
                </div>
              </section>

              <div className="panel" style={{ marginTop: 16 }}>
                <div className="panel-header">
                  <div><h2>Plain-English summary</h2>
                  <span className="muted small">
                    Inquiries: {analysis.inquiries?.total_hard ?? 0} total · {analysis.inquiries?.attached_to_open_accounts ?? 0} attached · {analysis.inquiries?.standalone ?? 0} standalone
                    {" · "}Utilization: {analysis.tradeline_health?.revolving_utilization_pct ?? "—"}%
                    {" · "}Repair complexity: {analysis.repair_complexity?.level ?? "—"}
                  </span></div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="ghost-button" disabled={emailingAnalysis} onClick={emailAnalysis}>
                      <Mail size={14} aria-hidden="true" /> {emailingAnalysis ? "Sending…" : "Email to client"}
                    </button>
                    <button className="primary-button" disabled={matching} onClick={matchLenders}>
                      <Landmark size={14} aria-hidden="true" /> {matching ? "Matching…" : "Match lenders"}
                    </button>
                  </div>
                </div>
                <p className="body-copy" style={{ whiteSpace: "pre-line" }}>{analysis.client_summary}</p>
                {emailMsg && <p className="muted small">{emailMsg}</p>}
              </div>
            </>
          )}
          {!analysis && <p className="muted small" style={{ marginTop: 14 }}>No analysis yet — import a report and hit "Run qualification".</p>}
        </>
      )}

      {/* ─────────────── LENDER MATCH ─────────────── */}
      {tab === "applications" && client && <FundingApplications clientId={client.id} />}

      {tab === "lenders" && (
        <>
          <div className="panel">
            <div className="panel-header">
              <div><h2>Recommended lenders</h2>
              <span className="muted small">Ranked against the credit box of every active lender — score, state, revenue, time in business, industry, amount.</span></div>
              <button className="primary-button" disabled={matching} onClick={matchLenders}>
                <RefreshCw size={14} aria-hidden="true" /> {matching ? "Matching…" : matches.length ? "Re-run match" : "Match lenders"}
              </button>
            </div>
            {matchErr && <p className="error-text">{matchErr}</p>}
            <div className="table-list">
              {matches.map((m) => (
                <div className="table-row table-row--wide" key={m.id ?? m.lender_id} style={{ alignItems: "flex-start" }}>
                  <div style={{ minWidth: 54, textAlign: "center" }}>
                    <div style={{ fontFamily: "'Bebas Neue', Inter, sans-serif", fontSize: 26, color: m.match_score >= 70 ? "var(--ne-success-text)" : "var(--ne-chrome-2)" }}>{m.match_score}</div>
                    <div className="muted small">fit</div>
                  </div>
                  <div style={{ flex: "1 1 300px", minWidth: 0 }}>
                    <strong>{m.lender_name}</strong>{" "}
                    <span className="muted small">{m.lender_type} · {(m.products || []).join(", ")}</span>
                    {m.status && m.status !== "recommended" && (
                      <span className={`pill ${["approved", "funded"].includes(m.status) ? "pill-green" : m.status === "declined" ? "pill-red" : ""}`} style={{ marginLeft: 8 }}>
                        {m.status}{m.amount ? ` · ${money(m.amount)}` : ""}{m.outcome_at ? ` · ${String(m.outcome_at).slice(0, 10)}` : ""}
                      </span>
                    )}
                    <ul className="muted small" style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                      {m.reasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                    {m.warnings.length > 0 && (
                      <ul className="small" style={{ margin: "6px 0 0", paddingLeft: 18, color: "var(--ne-warning-text)" }}>
                        {m.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
                      </ul>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "stretch" }}>
                    {m.application_url && (
                      <a className="primary-button" style={{ textDecoration: "none" }} href={m.application_url} target="_blank" rel="noopener noreferrer">
                        Apply now <ExternalLink size={14} aria-hidden="true" />
                      </a>
                    )}
                    {m.id && (
                      <button className="ghost-button" onClick={() => setOutcomeMatch(m)}>
                        <CheckCircle2 size={14} aria-hidden="true" /> Record outcome
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {matches.length === 0 && !matchErr && (
                <p className="muted small">No matches yet. Run the qualification analysis, then hit "Match lenders".</p>
              )}
            </div>
          </div>

          {/* ─────────────── CK PRE-QUAL TIER (second opinion) ─────────────── */}
          {ck && (
            <div className="panel" style={{ marginTop: 16 }}>
              <div className="panel-header">
                <div><h2>Credit Karma pre-quals</h2>
                <span className="muted small">
                  Personal funding only — CK's marketplace offers with real approval odds. Not our first-choice
                  lenders, but a bankable fallback, and for high-debt files the personal-loan offers here ARE the
                  paydown play.{ck.captured_at ? ` Captured ${String(ck.captured_at).slice(0, 10)}.` : ""}
                </span></div>
              </div>
              {(ck.borrowing_potential || ck.applications_in_progress.length > 0) && (
                <p className="body-copy" style={{ marginBottom: 10 }}>
                  {ck.borrowing_potential ? <>CK borrowing potential: <strong>{money(ck.borrowing_potential)}</strong> in personal loans. </> : null}
                  {ck.applications_in_progress.length > 0 && (
                    <span className="pill" style={{ marginLeft: 4 }}>
                      In progress: {ck.applications_in_progress.join(", ")}
                    </span>
                  )}
                </p>
              )}
              <div className="table-list">
                {ck.prequalifications.map((o, i) => (
                  <div className="table-row table-row--wide" key={i} style={{ alignItems: "flex-start" }}>
                    <span className="pill" style={{ minWidth: 86, textAlign: "center" }}>{o.kind === "loan" ? "loan" : "card"}</span>
                    <div style={{ flex: "1 1 300px", minWidth: 0 }}>
                      <strong>{o.name}</strong>
                      <div className="muted small" style={{ marginTop: 4 }}>
                        {o.kind === "loan"
                          ? [o.amount ? money(o.amount) : null, o.est_apr ? `${o.est_apr} est APR` : null,
                             o.monthly_payment ? `${o.monthly_payment}` : null, o.term_months ? `${o.term_months} mo` : null]
                              .filter(Boolean).join(" · ")
                          : [o.annual_fee ? `Annual fee ${o.annual_fee}` : null, o.apr ? `APR ${o.apr}` : null,
                             o.credit_limit ? `Limit ${o.credit_limit}` : null].filter(Boolean).join(" · ") || "—"}
                      </div>
                      {o.approval_odds && <div className="small" style={{ marginTop: 4, color: "var(--ne-success-text)" }}>{o.approval_odds}</div>}
                    </div>
                  </div>
                ))}
                {ck.prequalifications.length === 0 && <p className="muted small">No pre-qual offers in the latest capture.</p>}
              </div>
            </div>
          )}

          {/* ─────────────── APPROVAL DATABASE ─────────────── */}
          <div className="panel" style={{ marginTop: 16 }}>
            <div className="panel-header">
              <div><h2>Approval history</h2>
              <span className="muted small">Real outcomes with dates — this is what teaches the matcher which lenders are actually approving, and how fresh each win is.</span></div>
              <button className="ghost-button" onClick={() => setBulkOpen(true)}>
                <History size={14} aria-hidden="true" /> Add past approvals
              </button>
            </div>
            <div className="table-list">
              {approvalsList.map((a) => (
                <div className="table-row table-row--wide" key={a.id}>
                  <strong style={{ flex: "1 1 180px" }}>{a.lender_name}</strong>
                  <span className={`pill ${["approved", "funded"].includes(a.status) ? "pill-green" : a.status === "declined" ? "pill-red" : ""}`}>{a.status}</span>
                  <span style={{ fontWeight: 600, minWidth: 90 }}>{a.amount ? money(a.amount) : "—"}</span>
                  <span className="muted small" style={{ minWidth: 110 }}>{a.product ? a.product.replace(/_/g, " ") : "—"}</span>
                  <span className="muted small" style={{ minWidth: 90 }}>{a.outcome_at ? String(a.outcome_at).slice(0, 10) : "—"}</span>
                  {a.outcome_note && <span className="muted small" style={{ flexBasis: "100%" }}>{a.outcome_note}</span>}
                </div>
              ))}
              {approvalsList.length === 0 && (
                <p className="muted small">
                  Nothing recorded yet. Dump this client's past approvals (with dates) — every entry makes the recommendations smarter.
                </p>
              )}
            </div>
          </div>

          <div className="panel" style={{ marginTop: 16 }}>
            <div className="panel-header">
              <div><h2>Copy-paste panel</h2>
              <span className="muted small">Bank sites block embedding, so applications open in a new tab — keep this panel handy to fill them out fast.</span></div>
              <button className="ghost-button" onClick={() => { navigator.clipboard.writeText(copyPanelText); }}>
                <ClipboardCopy size={14} aria-hidden="true" /> Copy all
              </button>
            </div>
            <pre className="body-copy" style={{ whiteSpace: "pre-wrap", background: "var(--ne-black-2)", border: "1px solid var(--ne-line)", borderRadius: 8, padding: 14, fontSize: 13 }}>{copyPanelText}</pre>
          </div>
        </>
      )}

      {/* dialogs */}
      {client && (
        <>
          <EditClientDialog open={editOpen} client={client as any} onClose={() => setEditOpen(false)} onSaved={(u) => { applyClient(u); setEditOpen(false); }} />
          {reqDocsOpen && <RequestDocumentsDialog open clientId={client.id} clientEmail={client.email} onClose={() => setReqDocsOpen(false)} />}
        </>
      )}
      <SendIntakeDialog open={intakeOpen} clientId={id} defaultEmail={client?.email} defaultPhone={client?.phone} onClose={() => setIntakeOpen(false)} />
      <ParsedReportModal open={!!viewReportId} reportId={viewReportId} onClose={() => setViewReportId(null)} />
      <BusinessAppModal appId={openBizAppId} onClose={() => setOpenBizAppId(null)} />
      <RecordOutcomeDialog
        key={outcomeMatch?.id ?? "none"}
        match={outcomeMatch}
        onClose={() => setOutcomeMatch(null)}
        onSaved={async () => {
          loadApprovals();
          try {
            const r = await recsApi.latest(id);
            if (r.matches?.length) setMatches(r.matches);
          } catch {}
        }}
      />
      <PastApprovalsDialog
        open={bulkOpen}
        clientId={id}
        onClose={() => setBulkOpen(false)}
        onSaved={loadApprovals}
      />
    </Shell>
  );
}
